/**
 * ENG-5641 — connectivity-probe strategy resolver.
 *
 * Maps an installed integration to the *simplest read-only reachability probe*
 * for its toolkit, returning a declarative {@link ConnectivityProbeDescriptor}.
 * The descriptor says WHAT kind of probe to run and WHERE it can run; the
 * actual call lives in the executors so this module stays pure, dependency-free
 * and unit-testable, and so provider-specific auth nuances (Linear's raw-key vs
 * Bearer scheme, Composio's user_id binding, the MCP handshake) stay in the one
 * place they're already tested:
 *
 *   - The manager CLI (host-side) interprets every kind — it runs where the
 *     agent's real credentials, network egress and live MCP servers are, so it
 *     can probe all four `toolkit_definitions.source_type`s honestly.
 *   - The API `POST /integrations/:id/test` endpoint interprets only the
 *     centrally-reachable kinds (see {@link ConnectivityProbeDescriptor.centralReachable}).
 *
 * INVARIANT: every probe is read-only / non-mutating. The resolver only ever
 * returns descriptors for cheap reads (list, viewer, --version, tools/list).
 * Executors MUST assert `descriptor.readOnly` before running anything.
 */

import type { IntegrationAuthType } from '../types/integration.js';
import { getOAuthProvider } from './oauth-providers.js';

/** `toolkit_definitions.source_type` (see 20250101000001_init.sql). */
export type ToolkitSourceType = 'managed' | 'mcp_server' | 'cli_tool' | 'native';

export type ConnectivityProbeKind =
  /** Provider-specific read-only HTTP check (Linear viewer, Google userinfo, …). Executor owns the call. */
  | 'http_provider'
  /** Managed/Composio: verify the connected account is ACTIVE and bound to the agent's runtime user_id. */
  | 'composio_account'
  /**
   * Managed/Composio host-side: MCP `tools/list` handshake AND the connected-
   * account binding check, combined (worst signal wins). The handshake catches
   * network / token-injection / MCP-URL drift; the account check catches a
   * dead/mis-bound connection that `tools/list` reads green on (ENG-6139).
   */
  | 'managed_composite'
  /** Remote streamable-HTTP MCP server: `initialize` → `tools/list` handshake over HTTP. */
  | 'mcp_tools_list'
  /**
   * Local STDIO MCP server (origami, xero, augmented-*): the manager spawns the
   * toolkit's bundled server with the agent's env, runs `initialize` →
   * `tools/list`, and (when the toolkit defines a read-only `connectivity_test`
   * tool) `tools/call`s it — the honest host-side reachability + auth check for
   * a stdio server whose live pipes only Claude Code holds (ENG-7405). Host-only.
   */
  | 'mcp_stdio'
  /** Native CLI tool: run a read-only command (e.g. `--version`, `whoami`). */
  | 'cli_command'
  /** Built-in / in-process module: a local reachability check. */
  | 'builtin'
  /** No connectivity probe is available for this integration. */
  | 'unsupported';

/** Latest single connectivity observation — mirrors the `last_connectivity_status` column (ENG-5641). */
export type ConnectivityStatus = 'ok' | 'degraded' | 'transient_error' | 'down';

export interface ConnectivityProbeDescriptor {
  kind: ConnectivityProbeKind;
  sourceType: ToolkitSourceType;
  /**
   * Always true. Present so executors can assert the invariant and refuse to
   * run any probe that somehow isn't a read.
   */
  readOnly: true;
  /** Human-readable label for logs / UI, e.g. "Linear: viewer query". */
  label: string;
  /**
   * Whether this probe can run from the central control plane (the API), or
   * only host-side in the manager CLI. The manager runs every kind; the API
   * only runs probes where it can reach the provider with the right identity.
   *
   * `mcp_tools_list`, `cli_command` and `builtin` are host-only by design —
   * a central Lambda has neither the agent's stdio MCP process, its shell, nor
   * its exact network/credential position, and probing centrally would report
   * green on something it never actually touched.
   */
  centralReachable: boolean;
  /** For `http_provider`: which provider check the executor should run (the `definition_id`). */
  httpProvider?: string;
  /** For `cli_command`: the read-only args, e.g. `['--version']`. */
  cliArgs?: string[];
  /**
   * ENG-6212 — for `managed_composite`: an operator-stored OVERRIDE tool to call
   * live (e.g. `GMAIL_GET_PROFILE`) instead of letting the probe auto-pick. This
   * is carried VERBATIM from `toolkit_definitions.connectivity_test.tool`; the
   * authoritative read-only gate is the probe itself (`resolveProbeTool` re-checks
   * it against the live tools/list and falls back to the heuristic on drift /
   * non-read-only), so the pure resolver does not — and cannot — validate it.
   */
  probeTool?: string;
  /** ENG-6212 — args for {@link probeTool} (managed/MCP). Default `{}`. */
  probeArgs?: Record<string, unknown>;
}

export interface ConnectivityProbeOutcome {
  status: ConnectivityStatus;
  message?: string;
  details?: Record<string, unknown>;
}

/** Severity ranking for {@link ConnectivityStatus} (higher = worse). */
const CONNECTIVITY_SEVERITY: Record<ConnectivityStatus, number> = {
  ok: 0,
  transient_error: 1,
  degraded: 2,
  down: 3,
};

/**
 * Return the more-severe of two probe outcomes (ENG-6139). Used by composite
 * probes (e.g. managed = MCP handshake + connected-account binding) so the
 * worst signal wins — a green handshake never masks a dead/mis-bound account.
 */
export function worseConnectivityOutcome(
  a: ConnectivityProbeOutcome,
  b: ConnectivityProbeOutcome,
): ConnectivityProbeOutcome {
  return CONNECTIVITY_SEVERITY[b.status] > CONNECTIVITY_SEVERITY[a.status] ? b : a;
}

/**
 * ENG-6212 — the shape stored in `toolkit_definitions.connectivity_test`: an
 * optional connectivity-test OVERRIDE. `tool` + `args` for managed/MCP toolkits
 * (call this specific read-only tool); `args` as a string[] for `cli_tool`
 * toolkits (run these read-only CLI args instead of the default `--version`).
 * Null/absent ⇒ the resolver and probe use their existing defaults.
 */
export interface ConnectivityTestOverride {
  /** managed/MCP: the specific tool to call (e.g. `GMAIL_GET_PROFILE`). */
  tool?: string | null;
  /** managed/MCP: an object of args for {@link tool}; cli_tool: a string[] of CLI args. */
  args?: Record<string, unknown> | string[] | null;
}

export interface ConnectivityProbeInput {
  /** Integration `definition_id`, e.g. 'linear', 'composio/gmail'. */
  definitionId: string;
  /** `toolkit_definitions.source_type`, when known. */
  sourceType?: ToolkitSourceType | null;
  /** The integration row's `auth_type`. */
  authType?: IntegrationAuthType | null;
  /**
   * ENG-6212 — the toolkit's `connectivity_test` override, when set. Carried onto
   * the descriptor (managed → probeTool/probeArgs; cli_tool → cliArgs). Null/absent
   * ⇒ heuristic pick (managed) / default `--version` (cli).
   */
  connectivityTest?: ConnectivityTestOverride | null;
}

/**
 * ENG-6042 / ENG-6428 — broker-managed toolkit ids → `cloud_account_enrolments.provider`
 * filter. These toolkits carry `auth_type='managed'` and `credentials {}` BY
 * DESIGN: the cloud-broker MCP mints scoped credentials per task at runtime, so
 * nothing is stored on the integration row and there is no static credential or
 * host-reachable endpoint to probe. The only meaningful signal is whether the
 * team/org actually has a broker enrolment, which is a CENTRAL check (see the
 * API's `testBrokerManagedToolkit`) — not a host-side reachability probe.
 *
 * `null` = no provider filter: `cloud-broker` is the shared broker MCP and serves
 * every provider the broker supports.
 *
 * Canonical here in core so the connectivity-probe resolver and the API Test path
 * read the SAME set — a drift between two copies is exactly what let broker
 * toolkits fall through to the Composio `managed_composite` probe and report a
 * perpetual false `transient_error` (ENG-6428). The API re-exports this.
 */
export const BROKER_TOOLKIT_PROVIDERS: Record<string, string | null> = {
  'aws-cli': 'aws',
  gcloud: 'gcp',
  'cloud-broker': null,
};

/** True when `definitionId` is a broker-managed toolkit (see {@link BROKER_TOOLKIT_PROVIDERS}). */
export function isBrokerToolkit(definitionId: string): boolean {
  return definitionId in BROKER_TOOLKIT_PROVIDERS;
}

/**
 * Definitions that are probed via a provider-specific read-only HTTP call by
 * the executors (the existing `testXConnection` helpers in the API route, and
 * the host-side equivalents). These are raw-token / direct-API providers, not
 * remote-MCP or Composio-managed.
 */
const HTTP_PROBE_PROVIDERS = new Set<string>([
  'linear',
  'google-workspace',
  'xero',
  'v0',
  // ENG-6642: Buffer is a direct-API GraphQL integration (api_key → Bearer),
  // not a CLI tool. Route it to the read-only org-details HTTP probe so health
  // reflects real API reachability, not a `buffer` binary on PATH. This wins
  // over its toolkit source_type, so the probe is honest regardless of how the
  // toolkit row is classified.
  'buffer',
  // ENG-6100: GitHub is deliberately NOT here. This set drives the ASYNC
  // connectivity monitor's routing, where github (source_type='native')
  // stays host-side (cli_command — `gh`, the credential the agent actually
  // executes with) rather than a central stored-token probe. The
  // synchronous Test button DOES probe the centrally-stored token via
  // `probeHttpProvider` (PROBE_DEFINITIONS in connectivity-http-probes.ts
  // includes 'github') — a narrower, honest "the token we stored is valid"
  // check. Unifying the monitor onto the central probe is sub-issue C's call.
]);

/** Read-only command args per CLI binary, keyed by `definition_id`. Default: `--version`. */
const CLI_PROBE_ARGS: Record<string, string[]> = {
  gcloud: ['version'],
  // ENG-6206: `gh --version` only proves the binary exists, not that it's
  // authenticated — so a missing/mis-named token read green (the false-green
  // that hid the broken fleet). `gh auth status` exits non-zero when not
  // logged in, the honest signal. Read-only. Requires the runner to pass the
  // agent's GH_TOKEN/GITHUB_TOKEN env (see manager-worker runCli wiring).
  github: ['auth', 'status'],
  // most CLIs respond to --version; override here only when they don't.
};

/**
 * Resolve the CLI probe args: a stored `connectivity_test.args` (string[]) wins
 * over the per-binary default. ENG-6212. NB: CLI args run straight to the host
 * shell with no live tools/list to re-validate against (unlike MCP tools), so a
 * stored CLI override is guarded ONLY at seed-time by the CI seed-lint — it must
 * stay read-only there.
 */
function cliArgsFor(definitionId: string, ct?: ConnectivityTestOverride | null): string[] {
  if (Array.isArray(ct?.args) && ct.args.length > 0 && ct.args.every((a) => typeof a === 'string')) {
    return ct.args as string[];
  }
  return CLI_PROBE_ARGS[definitionId] ?? ['--version'];
}

/**
 * Extract a managed/MCP override (tool name + object args) from the stored
 * `connectivity_test`. The tool is carried VERBATIM — the live read-only gate is
 * the probe (`resolveProbeTool`), not this pure resolver. ENG-6212.
 */
function mcpOverrideFrom(
  ct: ConnectivityTestOverride | null | undefined,
): { probeTool?: string; probeArgs?: Record<string, unknown> } {
  const tool = typeof ct?.tool === 'string' && ct.tool.trim().length > 0 ? ct.tool.trim() : undefined;
  if (!tool) return {};
  const args =
    ct?.args && typeof ct.args === 'object' && !Array.isArray(ct.args)
      ? (ct.args as Record<string, unknown>)
      : undefined;
  return { probeTool: tool, ...(args ? { probeArgs: args } : {}) };
}

/**
 * Resolve the connectivity probe strategy for an installed integration.
 *
 * Precedence is deliberate — `auth_type`/managed wins over `definition_id`,
 * which wins over remote-MCP, which wins over the raw `source_type` — so a
 * Composio-managed Linear install is probed as a Composio account (the real
 * signal), not as a raw Linear API call it has no key for.
 */
export function resolveConnectivityProbe(
  input: ConnectivityProbeInput,
): ConnectivityProbeDescriptor {
  const { definitionId, sourceType, authType } = input;

  // 0. Broker-managed toolkits (cloud-broker, aws-cli, gcloud) carry
  //    `auth_type='managed'` but are NOT Composio — the cloud-broker MCP mints
  //    scoped credentials per task at runtime, so there is no stored credential
  //    and no host-reachable endpoint to probe. Without this short-circuit they
  //    fell into the `managed_composite` branch below and the host probe ran a
  //    Composio MCP `tools/list` HTTP handshake against a server that doesn't
  //    exist for them — failing every cycle into a perpetual false
  //    `transient_error` (ENG-6428: 144 consecutive "failures" while the broker
  //    API was healthy). The honest host-side verdict is `unsupported`: skip,
  //    don't escalate. The real signal (broker enrolment presence) is a central
  //    check the API's `testBrokerManagedToolkit` already owns. We gate on
  //    `managed` so a non-broker install of the same definition_id (e.g. a raw
  //    OAuth `gcloud` CLI) keeps its normal source_type-based probe.
  if (isBrokerToolkit(definitionId) && (authType === 'managed' || sourceType === 'managed')) {
    return {
      kind: 'unsupported',
      sourceType: 'managed',
      readOnly: true,
      label: `${definitionId}: broker-managed — credentials minted per task; no host probe (central enrolment check only)`,
      centralReachable: false,
    };
  }

  // 1. Managed (Composio) toolkits are wired as remote MCP servers in the
  //    agent's .mcp.json (`composio_<toolkit>`), so the HONEST connectivity
  //    test is a host-side `tools/list` handshake against that server with the
  //    agent's injected token — exactly what a host probe is for. (ENG-5665)
  //
    //    ENG-6139: the MCP handshake alone is NOT sufficient. Composio returns a
    //    toolkit's tool list even when no connected account exists for the
    //    entity — only tool *calls* fail (`No connected account found for user
    //    id …`). So a dead/mis-bound account reads green on `tools/list` (the
    //    live sherlock incident). We now run BOTH host-side — the handshake
    //    (network / token-injection / MCP-URL drift, ENG-5665's valid point)
    //    AND the connected-account binding check — and take the worse outcome.
    //    The executor resolves the `composio_<toolkit>` server key from the
    //    target's `mcpServerKey` and the account inputs from its `credentials`;
    //    a missing capability degrades gracefully (never a false `down`).
  if (authType === 'managed' || sourceType === 'managed') {
    return {
      kind: 'managed_composite',
      sourceType: 'managed',
      readOnly: true,
      label: `${definitionId}: MCP tools/list + account binding (managed)`,
      centralReachable: false,
      // ENG-6212: carry the operator-stored override tool (if any) so both the
      // host executor and the central Test path call the same specific tool.
      ...mcpOverrideFrom(input.connectivityTest),
    };
  }

  // 2. Known raw-token / direct-API OAuth providers.
  if (HTTP_PROBE_PROVIDERS.has(definitionId)) {
    return {
      kind: 'http_provider',
      sourceType: sourceType ?? 'mcp_server',
      readOnly: true,
      label: `${definitionId}: read-only API check`,
      centralReachable: true,
      httpProvider: definitionId,
    };
  }

  // 3. Remote streamable-HTTP MCP providers (granola, …) — probe via an
  //    `initialize → tools/list` handshake against the provider's public MCP URL.
  //
  //    ENG-6396: this IS centrally reachable. Unlike Composio (where the bearer
  //    is injected on the host and the URL carries a host-resolved user_id),
  //    these providers store their OAuth `access_token` in the integration's
  //    `credentials` — exactly what the central control plane reads — and the
  //    `mcpUrl` is a fixed public endpoint. So the API's synchronous Test path
  //    can run the same `tools/list` probe with `Authorization: Bearer
  //    <access_token>`. Before this, the central path had no probe for these,
  //    fell through to a bare "Credentials present" non-verification, and a
  //    server-side-expired token sat looking healthy until the next live
  //    `tools/call` threw (the granola/Dwight incident). The host probe still
  //    runs this kind too (it runs every kind regardless of `centralReachable`).
  if (getOAuthProvider(definitionId)?.mcpUrl) {
    return {
      kind: 'mcp_tools_list',
      sourceType: sourceType ?? 'mcp_server',
      readOnly: true,
      label: `${definitionId}: MCP tools/list`,
      centralReachable: true,
    };
  }

  // 4. Fall back to the toolkit source_type.
  switch (sourceType) {
    case 'mcp_server':
      // ENG-5677/ENG-7405: step 3 already routed remote streamable-HTTP MCP
      // servers (those with a registered `mcpUrl`) to `mcp_tools_list`.
      // Reaching this branch means `source_type='mcp_server'` with NO remote
      // URL — a local-STDIO MCP server (origami, and any bundled/spawned
      // stdio toolkit). ENG-7405 replaced the old `unsupported` no-op with a
      // real `mcp_stdio` probe: the manager spawns the toolkit's bundled
      // server with the agent's env and runs initialize → tools/list (→ the
      // read-only `connectivity_test` tool when set). The override is carried
      // through so a stdio toolkit that names a free read (origami
      // `get_credit_balance`) gets a real execute-level check, not just a
      // handshake.
      return {
        kind: 'mcp_stdio',
        sourceType: 'mcp_server',
        readOnly: true,
        label: `${definitionId}: local-stdio MCP handshake${input.connectivityTest?.tool ? ` + ${input.connectivityTest.tool}` : ''}`,
        centralReachable: false,
        ...mcpOverrideFrom(input.connectivityTest),
      };
    case 'cli_tool':
      return {
        kind: 'cli_command',
        sourceType: 'cli_tool',
        readOnly: true,
        label: `${definitionId}: CLI reachability`,
        centralReachable: false,
        cliArgs: cliArgsFor(definitionId, input.connectivityTest),
      };
    case 'native':
      return {
        kind: 'builtin',
        sourceType: 'native',
        readOnly: true,
        label: `${definitionId}: built-in check`,
        centralReachable: false,
      };
    default:
      return {
        kind: 'unsupported',
        sourceType: sourceType ?? 'native',
        readOnly: true,
        label: `${definitionId}: no connectivity probe available`,
        centralReachable: false,
      };
  }
}

/** True when the API control plane can run this probe itself (vs. host-only). */
export function isCentrallyProbeable(descriptor: ConnectivityProbeDescriptor): boolean {
  return descriptor.centralReachable && descriptor.kind !== 'unsupported';
}
